import { css } from '@emotion/react';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { FC, useContext, useState, Dispatch, SetStateAction } from 'react';

import { Notification } from '@features/notifications';
import NotificationListItem from '@features/notifications/components/NotificationListItem/NotificationListItem';
import { staticFileLocation } from '@server/constants';
import { useCreateApplicationNote } from '@features/notifications/hooks/useReadNotification';
import { LoadingButton } from '@mui/lab';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIosOutlinedIcon from '@mui/icons-material/ArrowForwardIosOutlined';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import { Application } from '@features/applications';
import { authContext } from '@staffedupusa/shared-components';
import {
  getGroupSummaryText,
  groupNotificationsSmart,
} from '../NotificationListItem/utils';
import NotificationIcon from '../NotificationIcon/NotificationIcon';
import { ArrowDropDown } from '@mui/icons-material';

type NotificationsModalProps = {
  isOpen: boolean;
  handleClose: () => void;
  handleNotificationClick?: (application: Application) => void;
  setCongratulationsModal?: Dispatch<SetStateAction<boolean>>;
  handlePendingJobOfferNotificationClick?: (application: Application) => void;
  hanldeOpenThreads?: (application: Application) => void;
  notifications: Notification[];
  setNotifications: Dispatch<SetStateAction<Notification[]>>
};

const NotificationsModal: FC<NotificationsModalProps> = ({
  isOpen,
  setCongratulationsModal,
  handleNotificationClick,
  hanldeOpenThreads,
  handlePendingJobOfferNotificationClick,
  handleClose,
  notifications,
  setNotifications
}) => {
  const [errorText, setErrorText] = useState('');
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(
    {},
  );
  const theme = useTheme();
  const { authData } = useContext(authContext);
  const isApplicant =
    authData?.user &&
    !authData?.user?.company &&
    !authData?.user.enterprise &&
    !authData?.user.is_admin &&
    !authData?.user.is_rockerbox;
  const isCompany = authData?.user && authData?.user?.company;

  const { readNotification, isReadNotificationLoading } =
    useCreateApplicationNote({ setErrorText });

  const groupedNotifications = groupNotificationsSmart(notifications);
  const toggleGroup = (type: string) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [type]: !prev[type],
    }));
  };

  const handleClearAll = () => {
    const notificationIds = notifications.map(
      (notification) => notification.id,
    );
    setNotifications([]);
    const idsString = notificationIds.join(',');
    readNotification({
      notification_id: idsString,
      ...(!isApplicant ? { user_id: notifications[0].user_id } : {}),
    });
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth={notifications.length ? 'sm' : 'xs'}
      fullWidth
    >
      <DialogTitle>
        <IconButton
          sx={{ position: 'absolute', top: 5, right: 5 }}
          onClick={handleClose}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent style={{ paddingTop: '30px', paddingBottom: '30px' }}>
        <Stack spacing={2}>
          {errorText && <Alert severity="error">{errorText}</Alert>}
          <Typography
            display="flex"
            justifyContent="center"
            alignItems="center"
            variant="h3"
            color="secondary"
            fontSize="26px"
            fontWeight="bold"
            paddingBottom="5px"
          >
            <img
              src={`${staticFileLocation}img/notifications-modal-heading.png`}
              css={css`
                max-width: 80px;
                width: 100%;
                margin-right: ${theme.spacing(2)};
              `}
              alt="Notifications"
            />
            Notifications
          </Typography>
          {notifications.length ? (
            <>
              <List>
                {Object.entries(groupedNotifications).map(([type, group]) => {
                  if (group.length >= 3 && isCompany) {
                    const isExpanded = expandedGroups[type];
                    const summaryText = getGroupSummaryText(type, group.length);

                    return (
                      <div key={type}>
                        <ListItem>
                          <ListItemIcon
                            sx={{ minWidth: '30px' }}
                            style={{ marginTop: '3px' }}
                          >
                            <NotificationIcon notification={group[0]} />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography
                                component="div"
                                dangerouslySetInnerHTML={{
                                  __html: summaryText,
                                }}
                              />
                            }
                            style={{ color: 'black' }}
                          />
                          <Button
                            variant="contained"
                            size="small"
                            sx={{ fontSize: '1rem !important' }}
                            startIcon={
                              isExpanded ? (
                                <ArrowDropDown sx={{ fontSize: '28px !important' }} />
                              ) : (
                                  <ArrowRightIcon sx={{ fontSize: '28px !important' }} />
                              )
                            }
                            onClick={() => toggleGroup(type)}
                          >
                            {isExpanded ? "Hide" : "View"}
                          </Button>
                        </ListItem>
                        <Box sx={{ paddingLeft: '15px' }}>
                          {isExpanded &&
                            group.map((notification) => (
                              <NotificationListItem
                                key={notification.id}
                                notification={notification}
                                setErrorText={setErrorText}
                                setCongratulationsModal={
                                  setCongratulationsModal
                                }
                                handleClose={handleClose}
                                handleNotificationClick={
                                  handleNotificationClick
                                }
                                hanldeOpenThreads={hanldeOpenThreads}
                                handlePendingJobOfferNotificationClick={
                                  handlePendingJobOfferNotificationClick
                                }
                                setNotifications={setNotifications}
                              />
                            ))}
                        </Box>
                      </div>
                    );
                  }

                  return group.map((notification) => (
                    <NotificationListItem
                      key={notification.id}
                      notification={notification}
                      setErrorText={setErrorText}
                      setCongratulationsModal={setCongratulationsModal}
                      handleClose={handleClose}
                      handleNotificationClick={handleNotificationClick}
                      hanldeOpenThreads={hanldeOpenThreads}
                      handlePendingJobOfferNotificationClick={
                        handlePendingJobOfferNotificationClick
                      }
                      setNotifications={setNotifications}
                    />
                  ));
                })}
              </List>
              <LoadingButton
                variant="outlined"
                loading={isReadNotificationLoading}
                startIcon={<CheckOutlinedIcon />}
                sx={{ alignSelf: 'center' }}
                onClick={handleClearAll}
              >
                Clear All
              </LoadingButton>
            </>
          ) : (
            <>
              <Alert severity="success">All notifications read</Alert>
              <Button variant="contained" onClick={handleClose}>
                Got it
              </Button>
            </>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default NotificationsModal;
